import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import "../styles/image-viewer.css";

const Modal = ({
  setSelectedImg,
  selectedImg,
  imageCount,
  currentIndex,
  onPrevious,
  onNext,
}) => {
  const closeButtonRef = useRef(null);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setSelectedImg(null);
      if (event.key === "ArrowLeft" && imageCount > 1) {
        event.preventDefault();
        onPrevious();
      }
      if (event.key === "ArrowRight" && imageCount > 1) {
        event.preventDefault();
        onNext();
      }
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);
    closeButtonRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [imageCount, onNext, onPrevious, setSelectedImg]);

  const closeModal = () => setSelectedImg(null);

  return (
    <motion.div
      className="image-viewer-backdrop"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) closeModal();
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
    >
      <motion.section
        className="image-viewer"
        role="dialog"
        aria-modal="true"
        aria-label="Portfolio image preview"
        initial={{ opacity: 0, y: 18, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.24, ease: "easeOut" }}
      >
        <header className="image-viewer__header">
          <div>
            <p className="image-viewer__eyebrow">Arpita's Makeover · Portfolio</p>
            <h2>Image preview</h2>
          </div>
          <div className="image-viewer__header-actions">
            {imageCount > 1 && (
              <span className="image-viewer__counter" aria-live="polite">
                {currentIndex + 1} <span>/</span> {imageCount}
              </span>
            )}
            <button
              ref={closeButtonRef}
              className="image-viewer__close"
              type="button"
              onClick={closeModal}
              aria-label="Close image preview"
              title="Close"
            >
              <i className="fas fa-times" aria-hidden="true" />
            </button>
          </div>
        </header>

        <div className="image-viewer__photo-frame">
          <img src={selectedImg} alt="Selected makeup portfolio look" />
          {imageCount > 1 && (
            <>
              <button
                className="image-viewer__navigation image-viewer__navigation--previous"
                type="button"
                onClick={onPrevious}
                aria-label="View previous image"
                title="Previous image"
              >
                <i className="fas fa-chevron-left" aria-hidden="true" />
                <span>Previous</span>
              </button>
              <button
                className="image-viewer__navigation image-viewer__navigation--next"
                type="button"
                onClick={onNext}
                aria-label="View next image"
                title="Next image"
              >
                <span>Next</span>
                <i className="fas fa-chevron-right" aria-hidden="true" />
              </button>
            </>
          )}
        </div>

        <footer className="image-viewer__footer">
          <span>Beauty, made for your moment.</span>
          <span className="image-viewer__hint">
            {imageCount > 1 && <><kbd>←</kbd><kbd>→</kbd> browse · </>}
            <kbd>Esc</kbd> to close
          </span>
        </footer>
      </motion.section>
    </motion.div>
  );
};

export default Modal;
